import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import axios from 'axios';
import QRCode from 'qrcode';
import StatusBadge from './StatusBadge';
import arrowLeft  from './images/assets/Arrow-left.svg';
import arrowRight from './images/assets/Arrow-right.svg';
import dropGoLogo from './images/logos/DropGoLogo2.svg';
import './Home.css';

const TAKE = 5;
const TODAY = new Date().toLocaleDateString('en-US', { weekday: 'long' }); // e.g. "Tuesday"

function StoreList() {
  // location state: 'detecting' | 'granted' | 'denied' | 'postal'
  const [locState,    setLocState]    = useState('detecting');
  const [coords,      setCoords]      = useState(null);   // { lat, lang }
  const [postalInput, setPostalInput] = useState('');
  const [postalQuery, setPostalQuery] = useState(null);   // committed postal search

  const [stores,      setStores]      = useState([]);
  const [total,       setTotal]       = useState(0);
  const [skip,        setSkip]        = useState(0);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');

  // ── Fetch stores ──────────────────────────────────────────────────────────
  const fetchStores = useCallback(async (newSkip = 0) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ skip: newSkip });
      if (postalQuery) {
        params.append('postal', postalQuery);
      } else if (coords) {
        params.append('lat',  coords.lat);
        params.append('lang', coords.lang);
      }
      const res = await axios.get(`/api/stores?${params}`);
      setStores(res.data.items);
      setTotal(res.data.total);
      setSkip(newSkip);
    } catch {
      setError('Unable to load nearby locations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [coords, postalQuery]);

  // ── Geolocation on mount ──────────────────────────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocState('denied');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => {
        setCoords({ lat: pos.coords.latitude, lang: pos.coords.longitude });
        setLocState('granted');
      },
      () => setLocState('denied'),
      { timeout: 8000 }
    );
  }, []);

  // ── Fetch when coords are ready ───────────────────────────────────────────
  useEffect(() => {
    if (coords) fetchStores(0);
  }, [coords]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch when postal query is committed ──────────────────────────────────
  useEffect(() => {
    if (postalQuery) fetchStores(0);
  }, [postalQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePostalSubmit = e => {
    e.preventDefault();
    const clean = postalInput.trim().replace(/\s/g, '').toUpperCase();
    if (clean.length >= 3) setPostalQuery(clean);
  };

  // ── Detecting ─────────────────────────────────────────────────────────────
  if (locState === 'detecting') {
    return <p className="locations-status">Detecting your location…</p>;
  }

  // ── Denied / postal fallback ──────────────────────────────────────────────
  if (locState === 'denied' && !postalQuery) {
    return (
      <div className="postal-fallback">
        <p>Enter your postal code to find nearby return locations.</p>
        <form className="postal-form" onSubmit={handlePostalSubmit}>
          <input
            type="text"
            placeholder="e.g. L9P 1C6"
            value={postalInput}
            onChange={e => setPostalInput(e.target.value)}
            maxLength={7}
          />
          <button type="submit" className="actionButton">Search</button>
        </form>
      </div>
    );
  }

  // ── Store list ────────────────────────────────────────────────────────────
  if (loading) return <p className="locations-status">Loading…</p>;
  if (error)   return <p className="locations-status locations-error">{error}</p>;
  if (!stores.length) return <p className="locations-status">No locations found.</p>;

  const start = skip + 1;
  const end   = Math.min(skip + TAKE, total);

  return (
    <div className="store-list-wrapper">
      <ul className="store-list">
        {stores.map(store => {
          const todayHours = store.storeHours?.find(h => h.weekDay === TODAY);
          return (
            <li key={store.id} className="store-item">
              <div className="store-item-header">
                <span className="store-name">{store.locationName}</span>
                {store.distance > 0 && (
                  <span className="store-distance">{store.distance.toFixed(1)} km</span>
                )}
              </div>
              <div className="store-address">
                {store.streetNo} {store.street}, {store.city}
              </div>
              {store.isTemporarilyClosed ? (
                <div className="store-hours store-hours--closed">Temporarily Closed</div>
              ) : todayHours ? (
                <div className="store-hours">
                  Today: {todayHours.fromHour} – {todayHours.toHour}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {total > TAKE && (
        <div className="store-pagination">
          <button
            className="store-pagination-btn"
            disabled={skip === 0}
            onClick={() => fetchStores(skip - TAKE)}
            aria-label="Previous">
            <img src={arrowLeft} alt="Previous" />
          </button>
          <span className="store-pagination-info">{start}–{end} of {total}</span>
          <button
            className="store-pagination-btn"
            disabled={skip + TAKE >= total}
            onClick={() => fetchStores(skip + TAKE)}
            aria-label="Next">
            <img src={arrowRight} alt="Next" />
          </button>
        </div>
      )}

      <form className="postal-form postal-form--inline" onSubmit={handlePostalSubmit}>
        <input
          type="text"
          placeholder={coords ? 'Search by postal code' : 'Try a different postal code'}
          value={postalInput}
          onChange={e => setPostalInput(e.target.value)}
          maxLength={7}
        />
        <button type="submit" className="actionButton">Search</button>
      </form>
    </div>
  );
}

// ── Label QR Modal ────────────────────────────────────────────────────────────

function LabelQrModal({ label, onClose }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!label || !canvasRef.current) return;
    const payload = btoa(JSON.stringify({
      command: 'print',
      payload: { correlationId: label.label_id },
    }));
    QRCode.toCanvas(canvasRef.current, payload, { width: 220, margin: 2 });
  }, [label]);

  if (!label) return null;

  return (
    <div className="label-qr-overlay" onClick={onClose}>
      <div className="label-qr-modal" onClick={e => e.stopPropagation()}>
        <button className="label-qr-close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <canvas ref={canvasRef} />
        <p className="label-qr-hint">Scan this QR code to print your return label.</p>
      </div>
    </div>
  );
}

// ── Home ──────────────────────────────────────────────────────────────────────

function Home() {
  const { tbsUser } = useContext(AuthContext);
  const navigate    = useNavigate();

  const [openLabels,    setOpenLabels]    = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(true);
  const [totals,        setTotals]        = useState(null);
  const [qrLabel,       setQrLabel]       = useState(null);
  const [bonus,         setBonus]         = useState(null);

  useEffect(() => {
    axios.get('/api/labels/open')
      .then(res => setOpenLabels(res.data))
      .catch(() => {})
      .finally(() => setLabelsLoading(false));

    axios.get('/api/transactions')
      .then(res => setTotals(res.data.totals))
      .catch(() => {});

    axios.get('/api/config')
      .then(res => {
        const cfg    = res.data;
        const pct    = cfg?.payment_bonus_pct?.value_num;
        const fromDt = cfg?.payment_bonus_from_dt?.value_char;
        const toDt   = cfg?.payment_bonus_to_dt?.value_char;
        if (pct && fromDt && toDt) {
          const todayStr = new Date().toLocaleDateString('en-CA');
          const active   = todayStr >= fromDt && todayStr <= toDt;
          setBonus({ pct: Number(pct), fromDt, toDt, active });
        }
      })
      .catch(() => {});
  }, []);

  const fmtDate = dt => new Date(dt).toLocaleDateString('en-CA', { dateStyle: 'medium' });
  // value_char date-only strings (YYYY-MM-DD) parse as UTC midnight, which can roll
  // back a day in local timezones — force local-time parsing before formatting.
  const fmtBonusDate = dt => new Date(`${dt}T00:00:00`).toLocaleDateString('en-CA', { dateStyle: 'medium' });
  const fmt     = val => `$${Number(val || 0).toFixed(2)}`;
  const fmtStat = val => Number(val || 0).toFixed(1);

  const totalContainers = totals?.total_containers ?? 0;
  const landfillKg = totalContainers / 63;
  const ghgKg      = totalContainers / 6.52;
  const energyKwh  = totalContainers / 2.6;

  return (
    <div className="home-page">
      {/* <div className="home-welcome">
        <h1>Welcome, {tbsUser?.first_name} {tbsUser?.last_name}</h1>
      </div> */}

      <section className="home-card home-cta">
        {bonus?.active && (
          <span className="home-bonus-ribbon">{parseFloat(bonus.pct)}% BONUS*</span>
        )}
        <div className="home-cta-banner">
          <img src={dropGoLogo} alt="" className="home-cta-logo" />
        </div>

        <div className="home-cta-content">
          <div className="home-cta-body">
            <h2>DROP & GO STARTS HERE</h2>
            <p>Enter your empty returns details, drop off your bags, and get your deposit refund by eTransfer or donate to charity.</p>
            {bonus?.active && (
              <p className="home-bonus-note">*Bonus active until {fmtBonusDate(bonus.toDt)}</p>
            )}
            <button className="actionButton home-cta-btn" onClick={() => navigate('/return')}>
              START RETURN
            </button>
          </div>

          {totals && (
            <div className="home-cta-stats">
              <div className="home-stat">
                <span className="home-stat-label">Total Earned</span>
                <span className="home-stat-value">{fmt(totals.total_earned)}</span>
              </div>
              <div className="home-stat-divider" />
              <div className="home-stat">
                <span className="home-stat-label">Pending</span>
                <span className="home-stat-value home-stat-value--pending">{fmt(totals.total_pending)}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="home-grid">
        <section className="home-card home-card--recycling">
          <h2>YOUR RECYCLING STATS</h2>
          <div className="recycling-grid">
            <div className="recycling-stat">
              <div className="recycling-stat-icon recycling-stat-icon--green">♻</div>
              <div className="recycling-stat-body">
                <span className="recycling-stat-value">{totals?.total_containers ?? 0}</span>
                <span className="recycling-stat-label">Containers recycled</span>
              </div>
            </div>
            <div className="recycling-stat">
              <div className="recycling-stat-icon recycling-stat-icon--amber">◎</div>
              <div className="recycling-stat-body">
                <span className="recycling-stat-value">{fmtStat(landfillKg)} kg</span>
                <span className="recycling-stat-label">Spared from the landfill</span>
              </div>
            </div>
            <div className="recycling-stat">
              <div className="recycling-stat-icon recycling-stat-icon--blue">☁</div>
              <div className="recycling-stat-body">
                <span className="recycling-stat-value">{fmtStat(ghgKg)} kg</span>
                <span className="recycling-stat-label">Of greenhouse gas saved</span>
              </div>
            </div>
            <div className="recycling-stat">
              <div className="recycling-stat-icon recycling-stat-icon--indigo">⚡</div>
              <div className="recycling-stat-body">
                <span className="recycling-stat-value">{fmtStat(energyKwh)} kWh</span>
                <span className="recycling-stat-label">Of energy saved</span>
              </div>
            </div>
          </div>
        </section>

        <section className="home-card home-card--locations">
          <h2>NEAREST BAG DROP LOCATIONS</h2>
          <StoreList />
        </section>
      </div>

      {!labelsLoading && openLabels.length > 0 && (
        <section className="open-returns">
          <h2>OPEN DROP & GO RETURNS</h2>
          <p className="open-returns-hint">Tap on a label to display QR code for printing</p>
          <div className="open-returns-scroll">
            <table>
              <thead>
                <tr>
                  <th>Label</th>
                  <th className="col-created">Created</th>
                  <th className="col-items">Qty</th>
                  <th>Value</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {openLabels.map(l => (
                  <tr key={l.label_id} className="open-return-row" onClick={() => setQrLabel(l)}>
                    <td>{l.label_id}</td>
                    <td className="col-created">{fmtDate(l.created_dtm)}</td>
                    <td className="col-items">{l.total_qty}</td>
                    <td>{fmt(l.total_value)}</td>
                    <td>
                      <StatusBadge
                        status={l.printed_dtm ? 'printed' : 'new'}
                        title={l.printed_dtm ? `Printed ${fmtDate(l.printed_dtm)}` : undefined}
                      />
                    </td>
                    <td className="open-returns-action">
                      {!l.printed_dtm && (
                        <button
                          className="edit-action-btn"
                          onClick={e => { e.stopPropagation(); navigate(`/return/${l.label_id}`); }}
                          aria-label="Edit label"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <LabelQrModal label={qrLabel} onClose={() => setQrLabel(null)} />
    </div>
  );
}

export default Home;
